import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Input, Row, Col, Form, Label } from "reactstrap";
import OpenTotalModal from "../OpenTotalModal";
import NumberFormat from "react-number-format";
import shallow from "zustand/shallow";
import useCurrentLeaseStore from "../../../../../stores/LeaseStore";
import ChildrenModal from "../../../../../components/modals/ChildrenModal";
import { DealStatus, LoanPeriod } from "../../../../../constants/Constants";
import useCurrentDealStore from "../../../../../stores/DealStore";
import { calculateResidualValue } from "../../../helperFunctions/LeaseFunctions";

const safeNumber = (v) => {
	const n = typeof v === "string" ? parseFloat(v) : Number(v);
	return Number.isFinite(n) ? n : 0;
};

const MileageModal = ({ isOpen, toggle, readOnly }) => {
	const lease = useCurrentLeaseStore((state) => ({ ...state }), shallow);

	// period term (months) with safety guard
	const periodTerm = useMemo(() => {
		const lp = lease?.leasePeriod;
		const period = lp != null ? LoanPeriod[lp] : undefined;
		const t = period?.term;
		return Number.isFinite(t) && t > 0 ? t : 0;
	}, [lease?.leasePeriod]);

	const years = useMemo(() => {
		const term = safeNumber(lease?.term);
		return periodTerm > 0 ? term / periodTerm : 0;
	}, [lease?.term, periodTerm]);

	const milesPerYear = safeNumber(lease?.milesPerYear);
	const allowableMilesPerYear = safeNumber(lease?.allowableMilesPerYear);

	const excessMilesPerYear = useMemo(
		() => Math.max(milesPerYear - allowableMilesPerYear, 0),
		[milesPerYear, allowableMilesPerYear]
	);

	const totExcessMiles = useMemo(
		() => Math.floor(excessMilesPerYear * years),
		[excessMilesPerYear, years]
	);

	const updateIfChanged = useCallback(
		(key, next) => {
			const current = safeNumber(lease?.[key]);
			if (current !== next) {
				lease.editField(key, next);
			}
		},
		[lease]
	);

	useEffect(() => {
		updateIfChanged("excessMilesPerYear", excessMilesPerYear);
	}, [excessMilesPerYear, updateIfChanged]);

	useEffect(() => {
		updateIfChanged("totExcessMiles", totExcessMiles);
	}, [totExcessMiles, updateIfChanged]);

	useEffect(() => {
		const perMileCharge = safeNumber(lease?.perMileCharge);
		const charge = Number((perMileCharge * totExcessMiles).toFixed(2));
		updateIfChanged("mileageCharge", charge);
		//		calculateResidualValue(lease.msrp, lease.residualFactor, charge);
	}, [lease?.perMileCharge, totExcessMiles, updateIfChanged]);

	return (
		<ChildrenModal
			modal={isOpen}
			toggle={toggle}
			size="lg"
			modalTitle="Mileage"
		>
			<Form>
				{/* Miles Per Year */}
				<div className="col-md-6 d-flex justify-content-center align-items-center form-group mb-0">
					<Label style={{ minWidth: 200 }} className="text-right">
						Estimate Miles Per Year:&nbsp;
					</Label>
					<NumberFormat
						name="milesPerYear"
						readOnly={readOnly}
						value={milesPerYear}
						decimalScale={0}
						fixedDecimalScale
						allowNegative={false}
						isNumericString
						customInput={Input}
						onFocus={(e) => e.target.select()}
						onValueChange={(values) =>
							lease.editField("milesPerYear", safeNumber(values.value))
						}
					/>
				</div>

				{/* Allowable Miles */}
				<div className="col-md-6 d-flex justify-content-center align-items-center form-group mb-0">
					<Label style={{ minWidth: 200 }} className="text-right">
						Allowable Miles Per Year:&nbsp;
					</Label>
					<NumberFormat
						name="allowableMilesPerYear"
						readOnly={readOnly}
						value={allowableMilesPerYear}
						decimalScale={0}
						fixedDecimalScale
						allowNegative={false}
						isNumericString
						customInput={Input}
						onFocus={(e) => e.target.select()}
						onValueChange={(values) =>
							lease.editField("allowableMilesPerYear", safeNumber(values.value))
						}
					/>
				</div>

				{/* Excess Miles */}
				<div className="col-md-6 d-flex justify-content-center align-items-center form-group mb-0">
					<Label style={{ minWidth: 200 }} className="text-right">
						Excess Miles Per Year:&nbsp;
					</Label>
					<NumberFormat
						name="excessMilesPerYear"
						readOnly
						value={excessMilesPerYear}
						decimalScale={0}
						fixedDecimalScale
						isNumericString
						customInput={Input}
						onFocus={(e) => e.target.select()}
					/>
				</div>

				{/* Years */}
				<div className="col-md-6 d-flex justify-content-center align-items-center form-group mb-0">
					<Label style={{ minWidth: 200 }} className="text-right">
						Number of Years:&nbsp;
					</Label>
					<NumberFormat
						name="numOfYears"
						readOnly
						value={Math.floor(years)}
						decimalScale={0}
						fixedDecimalScale
						isNumericString
						customInput={Input}
						onFocus={(e) => e.target.select()}
					/>
				</div>

				{/* Total Excess Miles */}
				<div className="col-md-6 d-flex justify-content-center align-items-center form-group mb-0">
					<Label style={{ minWidth: 200 }} className="text-right">
						Total Excess Miles:&nbsp;
					</Label>
					<NumberFormat
						name="totExcessMiles"
						readOnly
						value={totExcessMiles}
						decimalScale={0}
						fixedDecimalScale
						isNumericString
						customInput={Input}
						onFocus={(e) => e.target.select()}
					/>
				</div>

				{/* Per Mile Charge */}
				<div className="col-md-6 d-flex justify-content-center align-items-center form-group mb-0">
					<Label style={{ minWidth: 200 }} className="text-right">
						Charge Per Excess Mile:&nbsp;
					</Label>
					<NumberFormat
						name="perMileCharge"
						readOnly={readOnly}
						value={safeNumber(lease?.perMileCharge)}
						decimalScale={2}
						fixedDecimalScale
						isNumericString
						allowNegative={false}
						prefix="$"
						customInput={Input}
						onFocus={(e) => e.target.select()}
						onValueChange={(values) =>
							lease.editField("perMileCharge", safeNumber(values.value))
						}
					/>
				</div>

				{/* Total Mileage Charge */}
				<div className="col-md-6 d-flex justify-content-center align-items-center form-group mb-0">
					<Label style={{ minWidth: 200 }} className="text-right">
						Total Mileage Charge:&nbsp;
					</Label>
					<NumberFormat
						name="mileageCharge"
						readOnly
						value={safeNumber(lease?.mileageCharge)}
						thousandSeparator
						decimalScale={2}
						fixedDecimalScale
						isNumericString
						prefix="$"
						customInput={Input}
						onFocus={(e) => e.target.select()}
					/>
				</div>
			</Form>
		</ChildrenModal>
	);
};

const Mileage = () => {
	const deal = useCurrentDealStore((state) => ({ ...state }), shallow);
	const lease = useCurrentLeaseStore((state) => ({ ...state }), shallow);

	const readOnly = deal?.deal?.type !== DealStatus.PENDING;
	const [isModalOpen, setIsModalOpen] = useState(false);

	const toggleModal = () => setIsModalOpen((s) => !s);

	return (
		<Row>
			{isModalOpen && (
				<MileageModal
					isOpen={isModalOpen}
					toggle={toggleModal}
					readOnly={readOnly}
				/>
			)}
			<Col xs={6} className="px-0">
				<OpenTotalModal
					btnClass="w-100 btn btn-primary btn-md px-0"
					label="Mileage"
					toggle={toggleModal}
				/>
			</Col>
			<Col xs={6} className="pr-0 pl-2">
				<NumberFormat
					value={safeNumber(lease?.mileageCharge)}
					thousandSeparator
					decimalScale={2}
					fixedDecimalScale
					prefix="$"
					displayType="input"
					customInput={Input}
					readOnly
					onClick={toggleModal}
				/>
			</Col>
		</Row>
	);
};

export default Mileage;
